import ATPProductsService from '@/services/atp-products'
import Utils from '@/utils/utils'
import * as types from '../mutations'

const state = {
  atpProducts: [],
  isLoadingATPProducts: false,
  totalATPProducts: 0,
  selectedProduct: null,
  hasLoadedProducts: false,
  updatedATPProductId: null,
  newATPProduct: null,
  isNewATPProductConnected: false,
  isConnectingAnotherProductOrPolling: false,
  pollingProductId: null
}

const getters = {
  atpProducts: (state) => state.atpProducts,
  isLoadingATPProducts: (state) => state.isLoadingATPProducts,
  totalATPProducts: (state) => state.totalATPProducts,
  selectedProduct: (state) => state.selectedProduct,
  hasLoadedProducts: (state) => state.hasLoadedProducts,
  updatedATPProductId: (state) => state.updatedATPProductId,
  newATPProduct: (state) => state.newATPProduct,
  isNewATPProductConnected: (state) => state.isNewATPProductConnected,
  isConnectingAnotherProductOrPolling: (state) => state.isConnectingAnotherProductOrPolling,
  pollingProductId: (state) => state.pollingProductId
}

const actions = {
  async loadATPProducts ({ commit }, payload) {
    const data = await ATPProductsService.loadATPProducts(payload)
    const products = data?.results || []
    const count = data?.count || 0

    commit(types.SET_ATP_PRODUCTS, products)
    commit(types.SET_ATP_PRODUCTS_COUNT, count)
  },

  setIsLoadingATPProducts ({ commit }, isLoading) {
    commit(types.SET_IS_LOADING_ATP_PRODUCTS, isLoading)
  },

  setATPProducts ({ commit }, products) {
    commit(types.SET_ATP_PRODUCTS, products)
  },

  setSelectedProduct ({ commit }, product) {
    commit(types.SET_SELECTED_PRODUCT, product)
  },

  async verifyATPProduct ({ commit }, payload) {
    const { productId } = payload
    const result = await ATPProductsService.verifyProduct(productId)
    if (result && Object.keys(result).length > 0) {
      const status = result?.status === 'verified' ? 'connected' : 'not connected'
      let updatedProduct = {
        ...result,
        created_at: Utils.formatDate(result?.created_at, false, true),
        status: status
      }
      if (state.isConnectingAnotherProductOrPolling && state.pollingProductId === updatedProduct?.id) {
        updatedProduct = {
          ...updatedProduct,
          status: 'In progress'
        }
      }
      commit(types.UPDATE_ATP_PRODUCT, updatedProduct)
      commit(types.UPDATE_ATP_PRODUCT_STATUS, status)
    }
  },

  async reconnectATPProduct ({ commit }, payload) {
    const { productId } = payload
    const result = await ATPProductsService.verifyProduct(productId)
    if (result && Object.keys(result).length > 0) {
      const status = result?.status === 'verified' ? 'connected' : 'not connected'
      const updatedProduct = {
        ...result,
        created_at: Utils.formatDate(result?.created_at, false, true),
        status
      }
      commit(types.RECONNECT_ATP_PRODUCT, updatedProduct)
      commit(types.SET_UPDATED_ATP_PRODUCT_ID, result?.id)
      commit(types.UPDATE_ATP_PRODUCT_STATUS, status)
    }
  },

  setUpdatedATPProductId ({ commit }, id) {
    commit(types.SET_UPDATED_ATP_PRODUCT_ID, id)
  },

  async createATPProduct ({ commit }, payload) {
    const { productId, gtin } = payload
    const newProduct = await ATPProductsService.createATPProduct(productId, gtin)
    commit(types.CREATE_ATP_PRODUCT, newProduct)
  },

  async updateATPProduct ({ commit }, payload) {
    const { productId, description } = payload
    const updatedProduct = await ATPProductsService.updateATPProduct(productId, description)
    commit(types.UPDATE_ATP_PRODUCT, updatedProduct)
  },

  async activateATPProduct ({ commit }, payload) {
    const { productId } = payload
    const updatedProduct = await ATPProductsService.activateProduct(productId)
    commit(types.ACTIVATE_ATP_PRODUCT, updatedProduct)
  },

  async deactivateATPProduct ({ commit }, payload) {
    const { productId } = payload
    const updatedProduct = await ATPProductsService.deactivateProduct(productId)
    commit(types.DEACTIVATE_ATP_PRODUCT, updatedProduct)
  },

  resetNewATPProduct ({ commit }) {
    commit(types.RESET_NEW_ATP_PRODUCT)
  },

  setIsConnectingAnotherProductOrPolling ({ commit }, payload) {
    commit(types.SET_IS_CONNECTING_ANOTHER_PRODUCT_OR_POLLING, payload)
  },

  setUpdateConnectedAtpProduct ({ commit }, payload) {
    commit(types.UPDATE_CONNECTED_ATP_PRODUCT, payload)
  },

  updateNewProductConnectedStatus ({ commit }, status) {
    commit(types.UPDATE_ATP_PRODUCT_STATUS, status)
  }
}

const mutations = {
  [types.SET_ATP_PRODUCTS] (state, products) {
    if (products.length > 0) {
      state.hasLoadedProducts = true
      state.atpProducts = products.map((obj) => {
        return {
          ...obj,
          product_name: obj?.product?.name,
          created_at: Utils.formatDate(obj?.created_at, false, true),
          sku: obj?.product?.sku,
          status: obj?.status === 'verified' ? 'connected' : 'not connected'
        }
      })
    } else {
      state.atpProducts = products
    }
  },

  [types.SET_IS_LOADING_ATP_PRODUCTS] (state, isLoading) {
    state.isLoadingATPProducts = isLoading
  },

  [types.SET_ATP_PRODUCTS_COUNT] (state, count) {
    state.totalATPProducts = count
  },

  [types.SET_SELECTED_PRODUCT] (state, product) {
    state.selectedProduct = product
  },

  [types.RECONNECT_ATP_PRODUCT] (state, updatedProduct) {
    const index = state.atpProducts.findIndex(atpProduct => atpProduct.id === updatedProduct.id)
    if (index !== -1) {
      Object.assign(state.atpProducts[index], updatedProduct)
    }
  },

  [types.SET_UPDATED_ATP_PRODUCT_ID] (state, id) {
    state.updatedATPProductId = id
  },

  [types.CREATE_ATP_PRODUCT] (state, newProduct) {
    state.newATPProduct = newProduct
  },

  [types.UPDATE_ATP_PRODUCT] (state, updatedProduct) {
    state.newATPProduct = {
      ...state.newATPProduct,
      ...updatedProduct,
      product_name: updatedProduct?.product?.name,
      created_at: Utils.formatDate(updatedProduct?.created_at, false, true),
      sku: updatedProduct?.product?.sku
    }
    const index = state.atpProducts.findIndex(atpProduct => atpProduct.id === updatedProduct.id)
    if (index !== -1) {
      Object.assign(state.atpProducts[index], state.newATPProduct)
    } else {
      state.atpProducts.unshift(state.newATPProduct)
    }
  },

  [types.ACTIVATE_ATP_PRODUCT] (state, updatedProduct) {
    const index = state.atpProducts.findIndex(atpProduct => atpProduct.id === updatedProduct.id)
    if (index !== -1) {
      const product = {
        ...updatedProduct,
        status: updatedProduct?.status === 'verified' ? 'connected' : 'not connected'
      }
      Object.assign(state.atpProducts[index], product)
    }
  },

  [types.DEACTIVATE_ATP_PRODUCT] (state, updatedProduct) {
    const index = state.atpProducts.findIndex(atpProduct => atpProduct.id === updatedProduct.id)
    if (index !== -1) {
      const product = {
        ...updatedProduct,
        status: updatedProduct?.status === 'verified' ? 'connected' : 'not connected'
      }
      Object.assign(state.atpProducts[index], product)
    }
  },

  [types.UPDATE_ATP_PRODUCT_STATUS] (state, status) {
    state.isNewATPProductConnected = status === 'connected'
  },

  [types.RESET_NEW_ATP_PRODUCT] (state) {
    state.newATPProduct = null
  },

  [types.SET_IS_CONNECTING_ANOTHER_PRODUCT_OR_POLLING] (state, payload) {
    const { productId, isConnectingOrPolling } = payload

    state.isConnectingAnotherProductOrPolling = isConnectingOrPolling
    state.pollingProductId = productId
  },

  [types.UPDATE_CONNECTED_ATP_PRODUCT] (state, updatedProduct) {
    const index = state.atpProducts.findIndex(atpProduct => atpProduct.id === updatedProduct.id)
    if (index !== -1) {
      Object.assign(state.atpProducts[index], updatedProduct)
    }
  }

}

export default {
  state,
  getters,
  actions,
  mutations
}
